import { FIXED_DT, FIXED_DT_MS, MAX_FRAME_DELTA_MS } from '../config';
import { World } from '../sim/world';
import { buildInputTimeline, worldFromScenario } from '../scenarios/replay';
import { neutralInput, type CarInput, type WorldState } from '../sim/types';
import type { Scenario } from '../scenarios/types';
import { DEFAULT_DEBUG_FLAGS, type DebugFlags } from '../render/debug/DebugOverlay';

/** Le rendu se branche au harness via cette interface (implémentée par GameScene). */
export interface Renderer {
  renderState(state: WorldState, debug: DebugFlags): void;
}

/**
 * Possède le World et orchestre l'avancée de la sim. En jeu réel, `advance(delta)`
 * fait tourner la boucle à pas fixe. En test (`manual = true`), SEUL `step(n)` avance
 * la physique → screenshots reproductibles (cf. PLAN.md §11).
 */
export class GameHarness {
  world: World;
  scenario: Scenario;
  currentInputs: CarInput[];
  timeline: CarInput[][] = [];
  useTimeline = false;
  manual = false;
  debug: DebugFlags = { ...DEFAULT_DEBUG_FLAGS };
  renderer: Renderer | null = null;

  readonly ready: Promise<void>;
  private resolveReady!: () => void;
  private acc = 0;

  constructor(initial: Scenario) {
    this.scenario = initial;
    this.world = worldFromScenario(initial);
    this.currentInputs = initial.cars.map(() => neutralInput());
    this.ready = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
  }

  /** Appelé par la scène une fois le rendu prêt. */
  markReady(): void {
    this.resolveReady();
  }

  /** (Re)charge un scénario : reconstruit le monde, réinitialise inputs et timeline. */
  reset(sc: Scenario): void {
    this.scenario = sc;
    this.world = worldFromScenario(sc);
    this.currentInputs = sc.cars.map(() => neutralInput());
    this.timeline = buildInputTimeline(sc, sc.cars.length);
    this.useTimeline = sc.inputs.length > 0;
    this.acc = 0;
    this.renderFromState();
  }

  private resolveInputs(): CarInput[] {
    if (this.useTimeline && this.timeline.length > 0) {
      return this.timeline[Math.min(this.world.step, this.timeline.length - 1)];
    }
    return this.currentInputs;
  }

  /** Un pas fixe de simulation. */
  tickOnce(): void {
    this.world.fixedUpdate(FIXED_DT, this.resolveInputs());
  }

  /** Boucle temps-réel : avance la sim selon le temps écoulé, à pas fixe. No-op si `manual`. */
  advance(deltaMs: number): void {
    if (this.manual) return;
    this.acc += Math.min(deltaMs, MAX_FRAME_DELTA_MS);
    while (this.acc >= FIXED_DT_MS) {
      this.tickOnce();
      this.acc -= FIXED_DT_MS;
    }
  }

  /** Avance d'EXACTEMENT `frames` pas (mode test), puis repeint. */
  step(frames = 1): void {
    for (let i = 0; i < frames; i++) this.tickOnce();
    this.renderFromState();
  }

  renderFromState(): void {
    this.renderer?.renderState(this.world.getState(), this.debug);
  }

  setDebug(flags: Partial<DebugFlags>): void {
    this.debug = { ...this.debug, ...flags };
    this.renderFromState();
  }

  getState(): WorldState {
    return this.world.getState();
  }

  hash(): string {
    return this.world.hash();
  }
}
